import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Sinistro {
  id: string;
  cliente_id: number;
  projeto_id?: number;
  pedido_id?: string;
  tipo?: string;
  descricao?: string;
  data_ocorrencia?: string;
  status: string;
  valor_estimado?: number;
  valor_aprovado?: number;
  itens_valor?: Array<{ id: string; descricao: string; valor: number }>;
  responsavel_id?: string;
  anexos?: string[];
  created_at?: string;
  updated_at?: string;
  
  // Relacionamentos opcionais
  cliente?: {
    id: number;
    nome: string;
    email?: string;
    telefone?: string;
    cidade?: string;
    estado?: string;
  };
  projeto?: {
    id: string;
    status: string;
  };
  pedido?: {
    id: string;
    numero_pedido: string;
  };
  responsavel?: {
    id: string;
    nome: string;
  };
}

export interface SinistroAnexo {
  id: string;
  sinistro_id: string;
  tipo: string;
  nome_arquivo?: string;
  url_arquivo?: string;
  descricao?: string;
  created_at: string;
}

export type SinistrosFiltroParams = {
  periodo?: { dataInicio?: string; dataFim?: string } | null; // sinistros.data_ocorrencia
  status?: string | null;
  tipo?: string | null;
  valorMin?: number | null; // sinistros.valor_estimado
  valorMax?: number | null;
  estado?: string | null; // via cliente
  cidade?: string | null; // via cliente
} | null;

// Hook para contar total de sinistros
export function useSinistrosCount() {
  return useQuery({
    queryKey: ['sinistros', 'count'],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('sinistros')
        .select('*', { count: 'exact', head: true });

      if (error) {
        console.error('❌ Erro ao contar sinistros:', error);
        throw error;
      }

      return count || 0;
    },
    staleTime: 60 * 1000, // 1 minuto - contagem muda menos frequentemente
    refetchOnWindowFocus: false,
  });
}

// Hook para buscar sinistros paginados
export function useSinistrosPaginated(page: number = 1, limit: number = 15, searchTerm?: string, filtros?: SinistrosFiltroParams) {
  return useQuery({
    queryKey: ['sinistros', 'paginated', page, limit, searchTerm, filtros ?? null],
    queryFn: async () => {
      console.log(`🔍 Buscando sinistros - Página ${page}, Limite ${limit}, Busca: ${searchTerm || 'nenhuma'}...`);
      
      try {
        const from = (page - 1) * limit;
        const to = from + limit - 1;

        const clienteFilterPresent = !!(
          (filtros?.estado && filtros.estado.trim()) ||
          (filtros?.cidade && filtros.cidade.trim())
        );

        // Construir query base
        let query = supabase
          .from('sinistros')
          .select(`
            *,
            cliente:clientes${clienteFilterPresent ? '!inner' : ''}(id, nome, email, telefone, cidade, estado)
          `, { count: 'exact' });

        // Filtros
        if (filtros?.periodo?.dataInicio) query = query.gte("data_ocorrencia", filtros.periodo.dataInicio);
        if (filtros?.periodo?.dataFim) query = query.lte("data_ocorrencia", filtros.periodo.dataFim);
        if (filtros?.status && filtros.status !== "todos") query = query.eq("status", filtros.status);
        if (filtros?.tipo && filtros.tipo.trim()) query = query.eq("tipo", filtros.tipo.trim());
        if (typeof filtros?.valorMin === "number") query = query.gte("valor_estimado", filtros.valorMin);
        if (typeof filtros?.valorMax === "number") query = query.lte("valor_estimado", filtros.valorMax);
        if (filtros?.estado && filtros.estado.trim()) query = query.eq("cliente.estado", filtros.estado.trim().toUpperCase());
        if (filtros?.cidade && filtros.cidade.trim()) query = query.eq("cliente.cidade", filtros.cidade.trim());

        // Aplicar busca se fornecido
        if (searchTerm && searchTerm.trim()) {
          const searchLower = searchTerm.trim();
          
          // Se for número, buscar por ID do sinistro ou projeto_id
          if (!isNaN(Number(searchLower))) {
            const numSearch = parseInt(searchLower);
            query = query.eq('id', numSearch);
          } else {
            // Se for texto, buscar por tipo ou buscar clientes que correspondem
            const { data: clientesEncontrados } = await supabase
              .from('clientes')
              .select('id')
              .or(`nome.ilike.%${searchLower}%`);
            
            if (clientesEncontrados && clientesEncontrados.length > 0) {
              const clienteIds = clientesEncontrados.map(c => c.id);
              query = query.or(`tipo.ilike.%${searchLower}%,cliente_id.in.(${clienteIds.join(',')})`);
            } else {
              query = query.ilike('tipo', `%${searchLower}%`);
            }
          }
        }

        // Ordenar e paginar
        query = query.order('id', { ascending: false }).range(from, to);

        const { data, error, count } = await query;

        if (error) {
          console.error('❌ ERRO na consulta de sinistros:', error);
          throw error;
        }

        console.log(`✅ Sinistros encontrados na página ${page}:`, data?.length || 0, `Total: ${count || 0}`);
        return { data: ((data || []) as unknown) as Sinistro[], count: count || 0 };
      } catch (error) {
        console.error('❌ ERRO GERAL em useSinistros:', error);
        throw error;
      }
    },
    staleTime: 30 * 1000, // 30 segundos
    refetchOnWindowFocus: false, // Desabilitado para reduzir egresso
    gcTime: 5 * 60 * 1000, // 5 minutos de cache
    retry: 2, // Reduzir tentativas
    retryDelay: 1000,
  });
}

// Hook legado - mantém contrato antigo retornando somente o array de sinistros
export function useSinistros(limit: number = 100) {
  return useQuery({
    queryKey: ['sinistros', 'default', limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sinistros')
        .select(`
          *,
          cliente:clientes(id, nome, email, telefone, cidade, estado)
        `)
        .order('id', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('❌ Erro ao buscar sinistros (hook simples):', error);
        throw error;
      }

      return (data || []) as Sinistro[];
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useSinistro(id: string) {
  return useQuery({
    queryKey: ['sinistro', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sinistros')
        .select(`
          *,
          cliente:clientes(*),
          projeto:projetos(*),
          pedido:pedidos(*),
          responsavel:profiles(*)
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('Erro ao buscar sinistro:', error);
        throw error;
      }

      return data as Sinistro;
    },
    enabled: !!id,
  });
}

export function useSinistroAnexos(sinistroId: string) {
  return useQuery({
    queryKey: ['sinistro-anexos', sinistroId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('sinistro_anexos')
        .select('*')
        .eq('sinistro_id', sinistroId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao buscar anexos do sinistro:', error);
        throw error;
      }

      return data as SinistroAnexo[];
    },
    enabled: !!sinistroId,
  });
}

export function useCriarSinistro() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sinistroData: {
      cliente_id: number;
      projeto_id?: number;
      pedido_id?: string;
      tipo?: string;
      descricao?: string;
      data_ocorrencia?: string;
      valor_estimado?: number;
      anexos?: string[];
    }) => {
      console.log('🔍 DEBUG - Dados recebidos em useCriarSinistro:', sinistroData);
      
      const dadosParaInserir: Record<string, unknown> = {
        cliente_id: sinistroData.cliente_id,
        tipo: sinistroData.tipo,
        descricao: sinistroData.descricao,
        data_ocorrencia: sinistroData.data_ocorrencia,
        valor_estimado: sinistroData.valor_estimado,
        status: 'aberto'
      };

      if (sinistroData.pedido_id) {
        dadosParaInserir.pedido_id = sinistroData.pedido_id;
      }

      if (sinistroData.anexos && sinistroData.anexos.length > 0) {
        dadosParaInserir.anexos = sinistroData.anexos;
      }

      console.log('🔍 DEBUG - Dados para inserir:', dadosParaInserir);

      try {
        const { data, error } = await supabase
          .from('sinistros')
          .insert(dadosParaInserir)
          .select()
          .single();

        if (error) {
          console.error('❌ ERRO Supabase:', error);
          console.error('❌ Código do erro:', error.code);
          console.error('❌ Mensagem do erro:', error.message);
          console.error('❌ Detalhes do erro:', error.details);
          throw error;
        }

        console.log('✅ Sinistro criado com sucesso:', data);
        return data;
      } catch (error) {
        console.error('❌ ERRO GERAL ao criar sinistro:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      console.log('✅ Sinistro criado com sucesso, invalidando queries...');
      console.log('✅ Dados do sinistro criado:', data);
      
      // Invalidar queries relacionadas
      queryClient.invalidateQueries({ queryKey: ['sinistros'] });
      
      // Forçar refetch imediato
      queryClient.refetchQueries({ queryKey: ['sinistros'] });
      
      console.log('✅ Queries invalidadas e refetch solicitado');
    },
    onError: (error) => {
      console.error('❌ ERRO no onError do useCriarSinistro:', error);
    }
  });
}

export function useAtualizarSinistro() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updateData }: { 
      id: string; 
      status?: string;
      descricao?: string;
      valor_estimado?: number | null;
      valor_aprovado?: number | null;
      itens_valor?: Array<{ id: string; descricao: string; valor: number }>;
      anexos?: string[];
      responsavel_id?: string;
    }) => {
      const { itens_valor, ...rest } = updateData;

      const runUpdate = async (includeItensValor: boolean) =>
        supabase
          .from('sinistros')
          .update({
            ...rest,
            ...(includeItensValor && itens_valor !== undefined ? { itens_valor } : {}),
          })
          .eq('id', id)
          .select(`
          *,
          cliente:clientes(id, nome, email, telefone, cidade, estado)
        `)
          .single();

      let { data, error } = await runUpdate(true);

      const itensValorColumnMissing =
        error?.code === 'PGRST204' &&
        (error.message?.includes('itens_valor') ?? false);

      if (itensValorColumnMissing && itens_valor !== undefined) {
        ({ data, error } = await runUpdate(false));
      }

      if (error) throw error;

      if (itensValorColumnMissing && itens_valor !== undefined && data) {
        return { ...data, itens_valor, _itensValorOmitido: true };
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sinistros'] });
      if (data?.id != null) {
        queryClient.invalidateQueries({ queryKey: ['sinistro', String(data.id)] });
      }
    }
  });
}

export function useAtualizarStatusSinistro() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, status }: { 
      id: string; 
      status: 'aberto' | 'analise' | 'andamento' | 'finalizado' | 'rejeitado';
    }) => {
      const { data: atual, error: readError } = await supabase
        .from('sinistros')
        .select('status')
        .eq('id', id)
        .single();

      if (readError) throw readError;
      if (atual?.status === 'finalizado') {
        throw new Error('Sinistro finalizado não pode ter o status alterado');
      }

      const { data, error } = await supabase
        .from('sinistros')
        .update({ status })
        .eq('id', id)
        .neq('status', 'finalizado')
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sinistros'] });
    }
  });
}

export type ExclusaoSinistroResult = {
  ok: boolean;
  sinistro_id: number;
  deleted: {
    sinistro_comentarios: number;
    transacoes_financeiras: number;
    sinistros: number;
  };
};

export function useExcluirSinistro() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sinistroId: number) => {
      const { data, error } = await supabase.rpc('excluir_sinistro', {
        p_sinistro_id: sinistroId,
      });
      if (error) throw error;
      return data as ExclusaoSinistroResult;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sinistros'] });
      queryClient.invalidateQueries({ queryKey: ['sinistro-comentarios'] });
      queryClient.invalidateQueries({ queryKey: ['transacoes_financeiras'] });
    },
  });
}
